import { Mesh, VertexData, type AbstractMesh, type Scene } from "@babylonjs/core";
import { SKULL_ORIGIN, SKULL_PATHS, clamp, smoothstep, valueNoise } from "@tiny-seas/shared";
import { skullFort } from "./bakes.ts";
import type { Materials } from "./materials.ts";

const WORN_COURTS = [
  [-29, -63, 17, 11], [16, 34, 18, 15], [-44, -95, 7, 5], [-31.5, 4, 3, 5],
] as const;

/** Blend wear into the actual terrain, with soft irregular edges and no floating decal plates. */
export function skullGroundWear(x: number, z: number): number {
  const noise = valueNoise(x * 0.45, z * 0.45, 417);
  let wear = 0;
  for (const path of SKULL_PATHS) {
    if (path.kind !== "ground") continue;
    for (let i = 1; i < path.points.length; i++) {
      const a = path.points[i - 1]!, b = path.points[i]!, dx = b.x - a.x, dz = b.z - a.z;
      const length2 = dx * dx + dz * dz;
      if (length2 < 0.01) continue;
      const t = clamp(((x - a.x) * dx + (z - a.z) * dz) / length2, 0, 1);
      const distance = Math.hypot(x - a.x - dx * t, z - a.z - dz * t);
      const half = path.width * (0.34 + noise * 0.04);
      wear = Math.max(wear, 1 - smoothstep(half * 0.35, half, distance));
    }
  }
  for (const [cx, cz, rx, rz] of WORN_COURTS) {
    const dx = (x - SKULL_ORIGIN.x - cx) / rx, dz = (z - SKULL_ORIGIN.z - cz) / rz;
    wear = Math.max(wear, 1 - smoothstep(0.55, 1.04, dx * dx + dz * dz + noise * 0.08));
  }
  return wear * 0.88;
}

/**
 * Skull's static salvage fort (geometry: `skull-fort.ts`, built off the main thread
 * by `loadGameAssets`). Every blocking structure is sourced from shared solids.
 */
export class SkullScene {
  constructor(scene: Scene, mats: Materials, addCaster: (mesh: AbstractMesh) => void) {
    for (const part of skullFort()) {
      const mesh = new Mesh(`skull:${part.key}`, scene), data = new VertexData();
      data.positions = part.positions; data.normals = part.normals; data.colors = part.colors;
      data.uvs = part.uvs; data.indices = part.indices; data.applyToMesh(mesh);
      mesh.material = mats.fortress(part.family);
      mesh.isPickable = false; mesh.receiveShadows = true; mesh.freezeWorldMatrix();
      if (part.shadows) addCaster(mesh);
    }
  }
}
